import React, { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useGoogleLogin } from '@react-oauth/google';
import { Button, Col, Divider, Row, } from 'antd';
import { LoginSocialApple } from 'reactjs-social-login';

import AuthForm from '../../components/AuthForm/AuthForm';
import AuthInfo from '../../components/AuthInfo/AuthInfo';

import { Apple, Google, Hand, } from '../../assets/icons';
import { Email } from '../../assets/icons/Email';

import { resetUserData } from '../../store/reducer/accountSlice';
import { realBricksApi } from '../../utils/axiosInterceptors';
import { toastNotify } from '../../utils/toastNotify';

import './SignupAuth.scss';
import { setCookieWithExpiryForReferralCode } from '../../utils/cookies';
import { isMobileDeviceDetectRegex } from '../../utils/Validations';
import { buildFirstTouchQueryParams, captureUtmParameters, getUtmParameters } from '../../utils/utmTracker';
import RegulatoryDisclosuresModal from '../../components/RegulatoryDisclosuresModal/RegulatoryDisclosuresModal';
import { generateBranchLinkAndRedirect, resetRedirectingFlag } from '../../utils/branchIoUtils';
import {handleLoginButtonNavigation} from '../../utils/handleLoginSignupRedirection'
import {BRANCHIO_REDIRECTION_PATH_NAME, BRANCHIO_FEATURE_NAME} from '../../utils/constant'
import { beginSocialSignup } from '../../utils/socialSignup';

export const SignupAuth = () => {
  const [viewForm, setViewForm] = useState(true);
  const [disclosureOpen, setDisclosureOpen] = useState(false);

  const userProfileData = useSelector((state) => state.userData.userData);

  const dispatch = useDispatch();
  let navigate = useNavigate();
  const shouldLog = useRef(true);
  const search = useLocation().search;
  const referralLink = new URLSearchParams(search).get('referredBy');

  const isMobileDevice = () => {
    return isMobileDeviceDetectRegex;
  };

  const signupWithEmailUrl = referralLink ? `/sign-up?referredBy=${referralLink}` : '/sign-up';

  useEffect(() => {
    captureUtmParameters();
    const utmParameters = getUtmParameters();
    const currentUrl = new URL(window.location.href);
    let hasUtmParams = false;

    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'gclid', 'fbclid', 'gbraid'];

    utmKeys.forEach(key => {
      if (utmParameters[key] && !currentUrl.searchParams.has(key)) {
        currentUrl.searchParams.set(key, utmParameters[key]);
        hasUtmParams = true;
      }
    });

    if (hasUtmParams) {
      navigate(currentUrl.pathname + currentUrl.search, { replace: true });
    }
  }, []);

  useEffect(() => {
    resetRedirectingFlag();
    if (isMobileDevice()) {
      const redirectIntendedPage = localStorage.getItem('redirectIntendedPage');
      if (!redirectIntendedPage) {
        generateBranchLinkAndRedirect(BRANCHIO_REDIRECTION_PATH_NAME.SIGN_UP_AUTH, { feature: BRANCHIO_FEATURE_NAME.SIGN_UP });
      }
      // If redirectIntendedPage exists in localStorage, don't redirect
    }
  }, []);

  useEffect(() => {
    if (shouldLog.current) {
      shouldLog.current = false;
      if (Object.keys(userProfileData).length && localStorage.getItem('access_token')) {
        navigate('/');
        toastNotify('error', 'You are already signed in. Please sign out in order to sign-up with different account.');
      }
    }
  }, []);

  // Preserve a ?referredBy= link from this page's URL into the referral cookie so it's still
  // picked up later by the Sign Up page (new social signups land there — see beginSocialSignup
  // below), even though this page no longer collects/displays referral info itself.
  useEffect(() => {
    if (referralLink) {
      setCookieWithExpiryForReferralCode('referralcode', referralLink, 60);
    }
  }, [referralLink]);

  const onAppleLoginSuccess = async (provider, data) => {
    const savedRedirectPath = localStorage.getItem('redirectIntendedPage');
    const utmParameters = getUtmParameters();
    dispatch(resetUserData());
    // localStorage.clear();
    const idToken = data?.authorization?.id_token;
    const queryParams = buildFirstTouchQueryParams(utmParameters);
    const queryString = queryParams.toString();
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/login/apple?loginType=Web${queryString ? `&${queryString}` : ''}`;

    try {
      const response = await realBricksApi.post(
        apiUrl,
        {},
        {
          headers: {
            Authorization: `${idToken}`,
          },
        }
      );
      if (savedRedirectPath) {
        localStorage.setItem('redirectIntendedPage', savedRedirectPath);
      }
      if (response?.data?.data?.user?.isSignUp) {
        localStorage.setItem('email', response?.data?.data?.user?.email);
        localStorage.setItem('access_token', response?.data?.data?.accessToken);
        localStorage.setItem('access_token_temp', response?.data?.data?.accessToken);
        localStorage.setItem('counter', response?.data?.data?.counter);
        if (response?.data?.data?.isNameSet === true) {
          if (response?.data?.data?.isPinSet === false) {
            navigate('/sign-up/createpin');
          } else {
            navigate('/login/enterpin');
          }
        } else {
          beginSocialSignup({ email: response?.data?.data?.user?.email, provider: 'apple' });
          navigate('/sign-up');
        }
      } else {
        localStorage.setItem('email', response?.data?.data?.email);
        localStorage.setItem('access_token', response?.data.data?.accessToken);
        localStorage.setItem('access_token_temp', response?.data.data?.accessToken);
        localStorage.setItem('counter', response?.data?.data?.counter);

        if (response?.data?.data?.isActive) {
          localStorage.setItem('email', response?.data?.data?.email);
          localStorage.setItem('access_token', response?.data?.data?.accessToken);
          localStorage.setItem('access_token_temp', response?.data.data?.accessToken);
          localStorage.setItem('counter', response?.data?.data?.counter);
          if (response?.data?.data?.isNameSet === true) {
            if (response?.data?.data?.isPinSet === false) {
              navigate('/sign-up/createpin');
            } else {
              navigate('/login/enterpin');
            }
          } else {
            beginSocialSignup({ email: response?.data?.data?.email, provider: 'apple' });
            navigate('/sign-up');
          }
        } else {
          navigate('/');
          toastNotify('error', 'Something went wrong');
        }
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    }
  };

  const onResolve = ({ provider, data }) => {
    onAppleLoginSuccess(provider, data);
  };

  const onLoginSuccess = async (response) => {
    const savedRedirectPath = localStorage.getItem('redirectIntendedPage');
    const utmParameters = getUtmParameters();
    dispatch(resetUserData());
    // localStorage.clear();
    const accessToken = response.access_token;

    const queryParams = buildFirstTouchQueryParams(utmParameters);
    const queryString = queryParams.toString();
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/login/google?loginType=Web` + (queryString ? `&${queryString}` : '');

    try {
      const userResponse = await realBricksApi.post(
        apiUrl,
        {},
        {
          headers: {
            Authorization: `${accessToken}`,
          },
        }
      );
      if (savedRedirectPath) {
        localStorage.setItem('redirectIntendedPage', savedRedirectPath);
      }
      if (userResponse.data.data?.user?.isSignUp) {
        localStorage.setItem('email', userResponse.data.data.user.email);
        localStorage.setItem('access_token', userResponse?.data?.data?.accessToken);
        localStorage.setItem('access_token_temp', userResponse?.data?.data?.accessToken);
        localStorage.setItem('counter', userResponse.data.data.counter);
        if (userResponse?.data?.data?.isNameSet === true) {
          if (userResponse?.data?.data?.isPinSet === false) {
            navigate('/sign-up/createpin');
          } else {
            navigate('/login/enterpin');
          }
        } else {
          beginSocialSignup({ email: userResponse.data.data.user.email, provider: 'google' });
          navigate('/sign-up');
        }
      } else {
        localStorage.setItem('access_token', userResponse?.data?.data?.accessToken);
        localStorage.setItem('access_token_temp', userResponse?.data?.data?.accessToken);
        localStorage.setItem('counter', userResponse.data.data.counter);
        localStorage.setItem('email', userResponse?.data?.data?.email);

        if (userResponse?.data?.data?.isActive) {
          localStorage.setItem('email', userResponse?.data?.data?.email);
          localStorage.setItem('access_token', userResponse?.data?.data?.accessToken);
          localStorage.setItem('counter', userResponse?.data?.data?.counter);
          if (userResponse.data.data.isNameSet === true) {
            if (userResponse.data.data.isPinSet === false) {
              navigate('/sign-up/createpin');
            } else {
              navigate('/login/enterpin');
            }
          } else {
            beginSocialSignup({ email: userResponse?.data?.data?.email, provider: 'google' });
            navigate('/sign-up');
          }
        } else {
          navigate('/');
          toastNotify('error', 'Something went wrong');
        }
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    }
  };

  const Googlelogin = useGoogleLogin({
    onSuccess: onLoginSuccess,
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    accessType: 'offline',
  });

  return (
    <div className='auth auth--details'>
      <AuthInfo
        className='hide'
        title={<>Real estate investing, one brick at a time</>}
        description={<>Start building your real estate portfolio, brick by brick.</>}
        note={
          <span className='underline cursor-pointer fw-600 auth__left__note' onClick={() => setDisclosureOpen(true)}>
            Company and Regulatory Disclosures
          </span>
        }
      >
        <div className='mobile-action' onClick={() => setViewForm(true)}>
          <Button className='w-100'>Get Started</Button>
        </div>
      </AuthInfo>
      <AuthForm
        className={viewForm ? 'show' : null}
        note={
          <>
            <span className='hide-mobile mr-4'>Already have an account?</span>
            <a href='/login' onClick={handleLoginButtonNavigation} className='underline fw-600'>
              Log in
            </a>
          </>
        }
        title={
          <>
            <Hand verticalAlign='-3px' marginRight='8px' />
            Welcome to Realbricks.
          </>
        }
      >
        <Row gutter={[16, 16]} className='mb-32 pt-10'>
          <Col span={24} lg={12}>
            <Button type='primary' className='ant-btn-primary--alt icon-btn' block onClick={() => Googlelogin()}>
              <Google size={16} /> Continue with Google
            </Button>
          </Col>
          <Col span={24} lg={12}>
            <LoginSocialApple
              client_id={process.env.REACT_APP_APPLE_CLIENT_ID}
              // scope='name email'
              redirect_uri={process.env.REACT_APP_APPLE_REDIRECT_URL}
              onResolve={onResolve}
              onReject={(err) => {
                toastNotify('error', err);
              }}
            >
              <Button type='primary' className='ant-btn-primary--alt icon-btn' block>
                <Apple size={16} />
                Continue with Apple
              </Button>
            </LoginSocialApple>
          </Col>
        </Row>
        <Divider plain>OR</Divider>
        <div className='text-center'>
          <Link to={signupWithEmailUrl} className='ant-btn ant-btn-primary ant-btn-primary--alt icon-btn email'>
            <Email size={16} /> Sign up With Email
          </Link>
        </div>
      </AuthForm>
      <RegulatoryDisclosuresModal open={disclosureOpen} onClose={() => setDisclosureOpen(false)} />
    </div>
  );
};
